import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  User, 
  Client, 
  Vehicle, 
  Driver, 
  Stop, 
  Route, 
  Job, 
  TraccarDevice, 
  TraccarTrip, 
  ApiResponse, 
  ClientDashboard
} from '../types';

// Create axios instance with base configuration
const createApiInstance = (): AxiosInstance => {
  const instance = axios.create({
    // Use relative path in production (same-origin), absolute URL in development
    baseURL: window.location.hostname === 'localhost' 
      ? 'http://fleet.host/api'
      : '/api',
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add token to requests if available
  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Handle responses and errors
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error) => {
      if (error.response?.status === 401) {
        // Don't redirect to login if we're already on the login endpoint
        const isLoginRequest = error.config?.url?.includes('/auth/login');
        
        if (!isLoginRequest) {
          // Unauthorized - clear token and redirect to login
          localStorage.removeItem('auth_token');
          window.location.href = '/login';
        }
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

const api = createApiInstance();

// Auth API
export const authApi = {
  login: async (credentials: { email: string; password: string }): Promise<ApiResponse<{ user: User; token: string }>> => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  register: async (userData: { name: string; email: string; password: string; role: string }): Promise<ApiResponse<User>> => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  verifyToken: async (token: string): Promise<ApiResponse<User>> => {
    const response = await api.get('/auth/verify', {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  logout: async (): Promise<ApiResponse<null>> => {
    const response = await api.post('/auth/logout');
    return response.data;
  },
};

// Vehicle API
export const vehicleApi = {
  getAll: async (): Promise<ApiResponse<Vehicle[]>> => {
    const response = await api.get('/vehicles');
    return response.data;
  },

  getById: async (id: number): Promise<ApiResponse<Vehicle>> => {
    const response = await api.get(`/vehicles/${id}`);
    return response.data;
  },

  create: async (vehicleData: Partial<Vehicle>): Promise<ApiResponse<Vehicle>> => {
    const response = await api.post('/vehicles', vehicleData);
    return response.data;
  },

  update: async (id: number, vehicleData: Partial<Vehicle>): Promise<ApiResponse<Vehicle>> => {
    const response = await api.put(`/vehicles/${id}`, vehicleData);
    return response.data;
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    const response = await api.delete(`/vehicles/${id}`);
    return response.data;
  },

  linkToTraccar: async (vehicleId: number, traccarDeviceId: number): Promise<ApiResponse<Vehicle>> => {
    const response = await api.post(`/vehicles/${vehicleId}/link-traccar`, { traccarDeviceId });
    return response.data;
  },
};

// Driver API
export const driverApi = {
  getAll: async (): Promise<ApiResponse<Driver[]>> => {
    const response = await api.get('/drivers');
    return response.data;
  },

  getById: async (id: number): Promise<ApiResponse<Driver>> => {
    const response = await api.get(`/drivers/${id}`);
    return response.data;
  },

  create: async (driverData: Partial<Driver>): Promise<ApiResponse<Driver>> => {
    const response = await api.post('/drivers', driverData);
    return response.data;
  },

  update: async (id: number, driverData: Partial<Driver>): Promise<ApiResponse<Driver>> => {
    const response = await api.put(`/drivers/${id}`, driverData);
    return response.data;
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    const response = await api.delete(`/drivers/${id}`);
    return response.data;
  },
};

// Stop API
export const stopApi = {
  getAll: async (): Promise<ApiResponse<Stop[]>> => {
    const response = await api.get('/stops');
    return response.data;
  },

  getById: async (id: number): Promise<ApiResponse<Stop>> => {
    const response = await api.get(`/stops/${id}`);
    return response.data;
  },

  create: async (stopData: Partial<Stop>): Promise<ApiResponse<Stop>> => {
    const response = await api.post('/stops', stopData);
    return response.data;
  },

  update: async (id: number, stopData: Partial<Stop>): Promise<ApiResponse<Stop>> => {
    const response = await api.put(`/stops/${id}`, stopData);
    return response.data;
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    const response = await api.delete(`/stops/${id}`);
    return response.data;
  },

  getVehicleStops: async (vehicleId: number): Promise<ApiResponse<Stop[]>> => {
    const response = await api.get(`/vehicles/${vehicleId}/stops`);
    return response.data;
  },
};

// Job API
export const jobApi = {
  getAll: async (): Promise<ApiResponse<Job[]>> => {
    const response = await api.get('/jobs');
    return response.data;
  },

  getById: async (id: number): Promise<ApiResponse<Job>> => {
    const response = await api.get(`/jobs/${id}`);
    return response.data;
  },

  create: async (jobData: Partial<Job>): Promise<ApiResponse<Job>> => {
    const response = await api.post('/jobs', jobData);
    return response.data;
  },

  update: async (id: number, jobData: Partial<Job>): Promise<ApiResponse<Job>> => {
    const response = await api.put(`/jobs/${id}`, jobData);
    return response.data;
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    const response = await api.delete(`/jobs/${id}`);
    return response.data;
  },

  assignToVehicle: async (jobId: number, vehicleId: number, driverId: number): Promise<ApiResponse<Job>> => {
    const response = await api.post(`/jobs/${jobId}/assign`, { vehicleId, driverId });
    return response.data;
  },

  updateStatus: async (jobId: number, status: string): Promise<ApiResponse<Job>> => {
    const response = await api.patch(`/jobs/${jobId}/status`, { status });
    return response.data;
  },
};

// Trip API
export const tripApi = {
  getAll: async (): Promise<ApiResponse<any[]>> => {
    const response = await api.get('/trips');
    return response.data;
  },

  getById: async (id: number): Promise<ApiResponse<any>> => {
    const response = await api.get(`/trips/${id}`);
    return response.data;
  },

  create: async (tripData: any): Promise<ApiResponse<any>> => {
    const response = await api.post('/trips', tripData);
    return response.data;
  },

  update: async (id: number, tripData: any): Promise<ApiResponse<any>> => {
    const response = await api.put(`/trips/${id}`, tripData);
    return response.data;
  },

  updateStopStatus: async (tripId: number, stopId: number, status: string): Promise<ApiResponse<null>> => {
    const response = await api.patch(`/trips/${tripId}/stops/${stopId}`, { status });
    return response.data;
  },

  autoUpdateProgress: async (tripId: number, latitude: number, longitude: number): Promise<ApiResponse<any>> => {
    const response = await api.patch(`/trips/${tripId}/auto-update`, { latitude, longitude });
    return response.data;
  },

  startTrip: async (tripId: number): Promise<ApiResponse<any>> => {
    const response = await api.patch(`/trips/${tripId}/start`);
    return response.data;
  },

  checkProgressFromHistory: async (tripId: number): Promise<ApiResponse<any>> => {
    const response = await api.patch(`/trips/${tripId}/check-progress`);
    return response.data;
  },

  getReplayPositions: async (tripId: number): Promise<ApiResponse<any>> => {
    const response = await api.get(`/trips/${tripId}/replay`);
    return response.data;
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    const response = await api.delete(`/trips/${id}`);
    return response.data;
  },
};

// Traccar API (through PHP proxy)
export const traccarApi = {
  getDevices: async (): Promise<ApiResponse<TraccarDevice[]>> => {
    const response = await api.get('/traccar/devices');
    return response.data;
  },

  getPositions: async (): Promise<ApiResponse<any[]>> => {
    const response = await api.get('/traccar/positions');
    return response.data;
  },

  getTrips: async (deviceId: number, from: string, to: string): Promise<ApiResponse<TraccarTrip[]>> => {
    const response = await api.get('/traccar/reports/trips', {
      params: { deviceId, from, to }
    });
    return response.data;
  },

  getRoute: async (deviceId: number, from: string, to: string): Promise<ApiResponse<any[]>> => {
    const response = await api.get('/traccar/reports/route', {
      params: { deviceId, from, to }
    });
    return response.data;
  },

  createGeofence: async (geofenceData: any): Promise<ApiResponse<any>> => {
    const response = await api.post('/traccar/geofences', geofenceData);
    return response.data;
  },

  getGeofences: async (): Promise<ApiResponse<any[]>> => {
    const response = await api.get('/traccar/geofences');
    return response.data;
  },

  syncWithTraccar: async (): Promise<ApiResponse<null>> => {
    const response = await api.post('/traccar/sync');
    return response.data;
  },
};

// Client API (for managing clients)
export const clientApi = {
  getAll: async (): Promise<ApiResponse<Client[]>> => {
    const response = await api.get('/clients');
    return response.data;
  },

  getById: async (id: number): Promise<ApiResponse<Client>> => {
    const response = await api.get(`/clients/${id}`);
    return response.data;
  },

  create: async (clientData: Partial<Client>): Promise<ApiResponse<Client>> => {
    const response = await api.post('/clients', clientData);
    return response.data;
  },

  update: async (id: number, clientData: Partial<Client>): Promise<ApiResponse<Client>> => {
    const response = await api.put(`/clients/${id}`, clientData);
    return response.data;
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    const response = await api.delete(`/clients/${id}`);
    return response.data;
  },

  getDashboard: async (clientId?: number): Promise<ApiResponse<ClientDashboard>> => {
    const url = clientId ? `/clients/${clientId}/dashboard` : '/clients/dashboard';
    const response = await api.get(url);
    return response.data;
  },

  getJobs: async (clientId: number): Promise<ApiResponse<Job[]>> => {
    const response = await api.get(`/clients/${clientId}/jobs`);
    return response.data;
  },

  getStops: async (clientId: number): Promise<ApiResponse<Stop[]>> => {
    const response = await api.get(`/clients/${clientId}/stops`);
    return response.data;
  },

  getRoutes: async (clientId: number): Promise<ApiResponse<Route[]>> => {
    const response = await api.get(`/clients/${clientId}/routes`);
    return response.data;
  },
};

// Route API
export const routeApi = {
  getAll: async (clientId?: number): Promise<ApiResponse<Route[]>> => {
    const params = clientId ? { client_id: clientId } : {};
    const response = await api.get('/routes', { params });
    return response.data;
  },

  getById: async (id: number): Promise<ApiResponse<Route>> => {
    const response = await api.get(`/routes/${id}`);
    return response.data;
  },

  create: async (routeData: Partial<Route>): Promise<ApiResponse<Route>> => {
    const response = await api.post('/routes', routeData);
    return response.data;
  },

  update: async (id: number, routeData: Partial<Route>): Promise<ApiResponse<Route>> => {
    const response = await api.put(`/routes/${id}`, routeData);
    return response.data;
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    const response = await api.delete(`/routes/${id}`);
    return response.data;
  },
};

export default api;